import DOMPurify from "dompurify";
import { sort } from "../../utils/utils";

const domPurifyConfig = { ALLOWED_TAGS: ["#text"], KEEP_CONTENT: true };
export const sanitizeStatement = (s) =>
  DOMPurify.sanitize(s, domPurifyConfig).trim();

export const getVersionHistory = (outcome) => {
  if (outcome.version_history.length === 0) {
    return [outcome];
  } else {
    return [outcome, getVersionHistory(outcome.version_history[0])].flat();
  }
};

export const getCurrentVersion = (outcome) => {
  const versionHistory = getVersionHistory(outcome);
  const now = new Date();
  for (let i = 0; i < versionHistory.length; i++) {
    if (
      versionHistory[i].effective_term === null ||
      new Date(versionHistory[i].effective_term?.start_at) <= now
    ) {
      return versionHistory[i];
    }
  }
};

export const getVersionByDate = (outcome, date) => {
  const versionHistory = getVersionHistory(outcome);
  for (let i = 0; i < versionHistory.length; i++) {
    if (
      versionHistory[i].effective_term === null ||
      new Date(versionHistory[i].effective_term?.start_at) <= date
    ) {
      return versionHistory[i];
    }
  }
};

export const getFutureVersions = (outcome) => {
  const versionHistory = getVersionHistory(outcome);
  const currentVersion = getCurrentVersion(outcome);
  const index = versionHistory.findIndex((v) => v.id === currentVersion.id);
  return versionHistory.slice(0, index);
};

export const getPastVersions = (outcome) => {
  const versionHistory = getVersionHistory(outcome);
  const currentVersion = getCurrentVersion(outcome);
  const index = versionHistory.findIndex((v) => v.id === currentVersion.id);
  return versionHistory.slice(index + 1);
};

export const hasUnmappedRatingScale = (outcome, mappings) => {
  let hasUnmapped = false;
  outcome.rating_scales.forEach((ratingScale) => {
    const mapping = mappings.find(
      (mapping) => mapping.id === ratingScale.rating_to_mastery_level_map
    );
    if (!mapping || mapping.is_mapped === false) {
      hasUnmapped = true;
    }
  });
  return hasUnmapped;
};

export const isArchived = (outcome) => {
  return (
    !!outcome.archive_term &&
    new Date(outcome.archive_term.start_at).setHours(0, 0, 0, 0) <=
      new Date().setHours(0, 0, 0, 0)
  );
};

/**
 * A category is treated as "effectively empty" when it has no active
 * (non-archived) outcomes. This covers two cases that should behave
 * identically: a category with zero outcomes, and a category whose
 * outcomes are all archived.
 */
export const hasNoActiveOutcomes = (category) => {
  const outcomes = category.outcomes ?? [];
  return outcomes.every((outcome) => isArchived(outcome));
};

export const isArchivedByDate = (outcome, date) => {
  return (
    !!outcome.archive_term && new Date(outcome.archive_term.start_at) <= date
  );
};

export const isFutureOutcome = (outcome) => {
  return (
    !!outcome.effective_term &&
    new Date(outcome.effective_term.start_at) >= new Date().setHours(0, 0, 0, 0)
  );
};

export const pendingArchive = (outcome) => {
  return (
    !!outcome.archive_term &&
    new Date(outcome.archive_term.start_at).setHours(0, 0, 0, 0) >
      new Date().setHours(0, 0, 0, 0)
  );
};

export const removeNewLines = (str) => {
  return str.replace(/(\r\n|\n|\r)/g, "");
};

export const sortOutcomes = (
  outcomes,
  getOutcome = (lo) => lo,
  nameProperty = "name",
  statementProperty = "statement"
) => {
  return sort(
    outcomes,
    (lo) =>
      `${getOutcome(lo)[nameProperty]}: ${getOutcome(lo)[statementProperty]}`
  );
};

export const getOriginalOutcomeVersion = (outcome) => {
  const versionHistory = getVersionHistory(outcome);
  return versionHistory[versionHistory.length - 1];
};

/**
 * Builds a map of sanitized outcome statements to their staleness classification.
 *
 * Shape:
 * {
 *   current: {
 *     versions:  string[]  // statements of non-archived current versions
 *     variants:  string[]  // statements from outcome.variants on the current version
 *   },
 *   past: {
 *     versions:  string[]  // statements from recursively traversed version_history
 *     variants:  string[]  // flattened variant statements found inside past versions
 *   }
 * }
 */
export const buildOutcomeStatementMap = (outcomes) => {
  const map = {
    current: { versions: [], variants: [] },
    past: { versions: [], variants: [] },
  };

  const addIfMissing = (arr, statement) => {
    const s = sanitizeStatement(statement);
    if (s && !arr.includes(s)) arr.push(s);
  };

  const collectPastVersions = (versionHistory) => {
    if (!versionHistory || versionHistory.length === 0) return;
    const head = versionHistory[0];
    addIfMissing(map.past.versions, head.statement);
    (head.variants || []).forEach((v) =>
      addIfMissing(map.past.variants, v.statement)
    );
    // version_history is a single-element array pointing to the next older version
    if (head.version_history && head.version_history.length > 0) {
      collectPastVersions(head.version_history);
    }
  };

  outcomes.forEach((outcome) => {
    if (!isArchived(outcome) && !isFutureOutcome(outcome)) {
      addIfMissing(map.current.versions, outcome.statement);
      (outcome.variants || []).forEach((v) =>
        addIfMissing(map.current.variants, v.statement)
      );
    } else if (isArchived(outcome)) {
      addIfMissing(map.past.versions, outcome.statement);
      (outcome.variants || []).forEach((v) =>
        addIfMissing(map.past.variants, v.statement)
      );
    }

    if (outcome.version_history && outcome.version_history.length > 0) {
      collectPastVersions(outcome.version_history);
    }
  });

  return map;
};

/**
 * Given a sanitized description string and the map from buildOutcomeStatementMap,
 * returns "current", "past_version", "past_variant", "current_variant", or null (no match).
 */
export const getOutcomeStaleness = (description, statementMap) => {
  if (statementMap.current.versions.includes(description)) return "current";
  if (statementMap.current.variants.includes(description))
    return "current_variant";
  if (statementMap.past.versions.includes(description)) return "past_version";
  if (statementMap.past.variants.includes(description)) return "past_variant";
  return null;
};
